import React, { useState, useEffect } from "react";
import {
  calculateCarAffordability,
  calculateHomeAffordability,
  CarAffordabilityInputs,
  HomeAffordabilityInputs,
} from "../utils/affordabilityCalculator";
import { useAuth } from "./AuthProvider";
import { getUser, patchUser, createCalculation, updateCalculation, isApiConfigured } from "../utils/api";
import type { CalculationType } from "../utils/api";
import CalculatorSeoSection from "./CalculatorSeoSection";
import LoanCalculatorTabs from "./LoanCalculatorTabs";
import AdSenseSlot from "./AdSenseSlot";
import SiteFooter from "./SiteFooter";
import "./Affordability.css";

export type AffordabilityMode = "car" | "home";

export type SavedAffordability = {
  activeCalculator?: "car" | "home";
  carPurchaseAmount?: string;
  carDeposit?: string;
  carInterestRate?: string;
  carRepaymentTerm?: string;
  carBalloonPayment?: string;
  carMonthlyIncome?: string;
  homeGrossMonthlyIncome?: string;
  homeGrossMonthlyIncome2?: string;
  homeDeposit?: string;
  homeInterestRate?: string;
  homeRepaymentTermYears?: string;
};

interface AffordabilityProps {
  mode: AffordabilityMode;
  onOpenAuthForSave?: (callback: () => void) => void;
  onOpenAuth?: () => void;
  pendingRestore?: { type: CalculationType; payload: Record<string, unknown>; calculationId?: string; name?: string; createdAt?: string } | null;
  onClearPendingRestore?: () => void;
}

const Affordability: React.FC<AffordabilityProps> = ({
  mode,
  onOpenAuthForSave,
  onOpenAuth,
  pendingRestore,
  onClearPendingRestore,
}) => {
  const { user } = useAuth();
  const isCar = mode === "car";

  const [carPurchaseAmount, setCarPurchaseAmount] = useState<string>("");
  const [carDeposit, setCarDeposit] = useState<string>("0");
  const [carInterestRate, setCarInterestRate] = useState<string>("10.25");
  const [carRepaymentTerm, setCarRepaymentTerm] = useState<string>("60");
  const [carBalloonPayment, setCarBalloonPayment] = useState<string>("0");
  const [carMonthlyIncome, setCarMonthlyIncome] = useState<string>("");

  const [homeGrossMonthlyIncome, setHomeGrossMonthlyIncome] =
    useState<string>("");
  const [homeGrossMonthlyIncome2, setHomeGrossMonthlyIncome2] =
    useState<string>("");
  const [homeDeposit, setHomeDeposit] = useState<string>("0");
  const [homeInterestRate, setHomeInterestRate] = useState<string>("10.25");
  const [homeRepaymentTermYears, setHomeRepaymentTermYears] =
    useState<string>("20");

  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [loadedCalculationId, setLoadedCalculationId] = useState<string | null>(null);
  const [loadedName, setLoadedName] = useState<string | null>(null);
  const [loadedCreatedAt, setLoadedCreatedAt] = useState<string | null>(null);

  const canSaveAsUser = Boolean(user && isApiConfigured());

  const formatLoadedDate = (iso: string) =>
    new Date(iso).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });

  const handleSaveClick = (forceNew = false) => {
    if (canSaveAsUser) {
      handleSave(forceNew);
    } else if (!user) {
      if (onOpenAuthForSave) {
        onOpenAuthForSave(() => handleSave(forceNew));
      } else {
        onOpenAuth?.();
      }
    }
  };

  useEffect(() => {
    if (!user || !isApiConfigured() || loadedOnce) return;
    setLoadedOnce(true);
    getUser()
      .then((data) => {
        const s = data.affordability as SavedAffordability | undefined;
        if (!s) return;
        if (s.carPurchaseAmount != null) setCarPurchaseAmount(s.carPurchaseAmount);
        if (s.carDeposit != null) setCarDeposit(s.carDeposit);
        if (s.carInterestRate != null) setCarInterestRate(s.carInterestRate);
        if (s.carRepaymentTerm != null) setCarRepaymentTerm(s.carRepaymentTerm);
        if (s.carBalloonPayment != null) setCarBalloonPayment(s.carBalloonPayment);
        if (s.carMonthlyIncome != null) setCarMonthlyIncome(s.carMonthlyIncome);
        if (s.homeGrossMonthlyIncome != null) setHomeGrossMonthlyIncome(s.homeGrossMonthlyIncome);
        if (s.homeGrossMonthlyIncome2 != null) setHomeGrossMonthlyIncome2(s.homeGrossMonthlyIncome2);
        if (s.homeDeposit != null) setHomeDeposit(s.homeDeposit);
        if (s.homeInterestRate != null) setHomeInterestRate(s.homeInterestRate);
        if (s.homeRepaymentTermYears != null) setHomeRepaymentTermYears(s.homeRepaymentTermYears);
      })
      .catch(() => setLoadedOnce(true));
  }, [user, loadedOnce]);

  useEffect(() => {
    if (!pendingRestore || !onClearPendingRestore) return;
    if (pendingRestore.type !== "affordability") return;
    const s = pendingRestore.payload as SavedAffordability;
    const restoreMode =
      s.activeCalculator === "home" ? "home" : "car";
    if (restoreMode !== mode) return;

    setLoadedOnce(true);
    if (pendingRestore.calculationId) setLoadedCalculationId(pendingRestore.calculationId);
    if (pendingRestore.name) setLoadedName(pendingRestore.name);
    if (pendingRestore.createdAt) setLoadedCreatedAt(pendingRestore.createdAt);
    if (s.carPurchaseAmount != null) setCarPurchaseAmount(String(s.carPurchaseAmount));
    if (s.carDeposit != null) setCarDeposit(String(s.carDeposit));
    if (s.carInterestRate != null) setCarInterestRate(String(s.carInterestRate));
    if (s.carRepaymentTerm != null) setCarRepaymentTerm(String(s.carRepaymentTerm));
    if (s.carBalloonPayment != null) setCarBalloonPayment(String(s.carBalloonPayment));
    if (s.carMonthlyIncome != null) setCarMonthlyIncome(String(s.carMonthlyIncome));
    if (s.homeGrossMonthlyIncome != null) setHomeGrossMonthlyIncome(String(s.homeGrossMonthlyIncome));
    if (s.homeGrossMonthlyIncome2 != null) setHomeGrossMonthlyIncome2(String(s.homeGrossMonthlyIncome2));
    if (s.homeDeposit != null) setHomeDeposit(String(s.homeDeposit));
    if (s.homeInterestRate != null) setHomeInterestRate(String(s.homeInterestRate));
    if (s.homeRepaymentTermYears != null) setHomeRepaymentTermYears(String(s.homeRepaymentTermYears));
    onClearPendingRestore();
  }, [pendingRestore, onClearPendingRestore, mode]);

  const handleSave = async (forceNew = false) => {
    if (!canSaveAsUser) return;
    setSaveStatus("saving");
    const payload: SavedAffordability = {
      activeCalculator: mode,
      carPurchaseAmount,
      carDeposit,
      carInterestRate,
      carRepaymentTerm,
      carBalloonPayment,
      carMonthlyIncome,
      homeGrossMonthlyIncome,
      homeGrossMonthlyIncome2,
      homeDeposit,
      homeInterestRate,
      homeRepaymentTermYears,
    };
    try {
      if (loadedCalculationId && !forceNew) {
        await updateCalculation(loadedCalculationId, { payload });
      } else {
        const created = await createCalculation({
          calculationType: "affordability",
          payload,
        });
        setLoadedCalculationId(created.calculationId);
        setLoadedName(created.name ?? null);
        setLoadedCreatedAt(created.createdAt ?? null);
      }
      await patchUser({ affordability: payload }).catch(() => {});
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2000);
    } catch {
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 2000);
    }
  };

  const carInputs: CarAffordabilityInputs = {
    purchaseAmount: parseFloat(carPurchaseAmount.replace(/[,\s]/g, "")) || 0,
    deposit: parseFloat(carDeposit.replace(/[,\s]/g, "")) || 0,
    interestRate: parseFloat(carInterestRate) || 0,
    repaymentTerm: parseInt(carRepaymentTerm) || 60,
    balloonPayment: parseFloat(carBalloonPayment.replace(/[,\s]/g, "")) || 0,
    monthlyIncome: parseFloat(carMonthlyIncome.replace(/[,\s]/g, "")) || 0,
  };

  const carResult = calculateCarAffordability(carInputs);

  const homeInputs: HomeAffordabilityInputs = {
    grossMonthlyIncome:
      (parseFloat(homeGrossMonthlyIncome.replace(/[,\s]/g, "")) || 0) +
      (parseFloat(homeGrossMonthlyIncome2.replace(/[,\s]/g, "")) || 0),
    deposit: parseFloat(homeDeposit.replace(/[,\s]/g, "")) || 0,
    interestRate: parseFloat(homeInterestRate) || 0,
    repaymentTermYears: parseInt(homeRepaymentTermYears) || 20,
    maxDebtToIncomeRatio: 30,
  };

  const homeResult = calculateHomeAffordability(homeInputs);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const hasValidCarInputs =
    carInputs.purchaseAmount > 0 &&
    carInputs.deposit >= 0 &&
    carInputs.interestRate > 0 &&
    carInputs.repaymentTerm > 0 &&
    carInputs.purchaseAmount > carInputs.deposit;

  const hasIncome = carInputs.monthlyIncome > 0;

  const hasValidHomeInputs =
    homeInputs.grossMonthlyIncome > 0 &&
    homeInputs.deposit >= 0 &&
    homeInputs.interestRate > 0 &&
    homeInputs.repaymentTermYears > 0 &&
    homeInputs.maxDebtToIncomeRatio > 0;

  const hasResult = isCar ? hasValidCarInputs : hasValidHomeInputs;

  const pageTitle = isCar ? "Car Affordability Calculator" : "Home Affordability Calculator";
  const pageSubtitle = isCar
    ? "Work out monthly repayments and whether a car fits your budget."
    : "Estimate how much home you can afford based on income and bond terms.";

  return (
    <main className="affordability">
      <div className="calculator-container">
        <div className="calculator-title-row">
          <div>
            <h1>{pageTitle}</h1>
            {loadedCalculationId && loadedName ? (
              <p className="loaded-file-label">
                {loadedName}{loadedCreatedAt ? ` – ${formatLoadedDate(loadedCreatedAt)}` : ""}
              </p>
            ) : (
              <p className="subtitle">{pageSubtitle}</p>
            )}
          </div>
        </div>

        <LoanCalculatorTabs />

        {isCar ? (
          <div className="calculator-section">
            <h2 className="visually-hidden">Car loan inputs</h2>

            <div className="input-group">
              <label htmlFor="car-purchase">Purchase Amount</label>
              <input
                id="car-purchase"
                type="text"
                value={carPurchaseAmount}
                onChange={(e) => setCarPurchaseAmount(e.target.value)}
                placeholder="e.g., 500,000"
              />
            </div>

            <div className="input-group">
              <label htmlFor="car-deposit">Deposit</label>
              <input
                id="car-deposit"
                type="text"
                value={carDeposit}
                onChange={(e) => setCarDeposit(e.target.value)}
                placeholder="e.g., 50,000"
              />
            </div>

            <div className="input-group">
              <label htmlFor="car-interest">Interest Rate (% p.a.)</label>
              <input
                id="car-interest"
                type="number"
                value={carInterestRate}
                onChange={(e) => setCarInterestRate(e.target.value)}
                placeholder="e.g., 13.5"
                step="0.01"
                min="0"
              />
            </div>

            <div className="input-group">
              <label htmlFor="car-term">Repayment Term (Months)</label>
              <select
                id="car-term"
                value={carRepaymentTerm}
                onChange={(e) => setCarRepaymentTerm(e.target.value)}
              >
                {[12, 24, 36, 48, 60, 72, 84].map((months) => (
                  <option key={months} value={months}>
                    {months} months ({months / 12}{" "}
                    {months / 12 === 1 ? "year" : "years"})
                  </option>
                ))}
              </select>
            </div>

            <div className="input-group">
              <label htmlFor="car-balloon">Balloon Payment (Residual)</label>
              <input
                id="car-balloon"
                type="text"
                value={carBalloonPayment}
                onChange={(e) => setCarBalloonPayment(e.target.value)}
                placeholder="e.g., 100,000"
              />
              <p className="input-hint">
                The amount you'll pay at the end of the loan term (optional)
              </p>
            </div>

            <div className="input-group">
              <label htmlFor="car-income">Monthly Net Income (Optional)</label>
              <input
                id="car-income"
                type="text"
                value={carMonthlyIncome}
                onChange={(e) => setCarMonthlyIncome(e.target.value)}
                placeholder="e.g., 30,000"
              />
              <p className="input-hint">
                Your take-home pay after all deductions. Leave blank to
                calculate repayment without affordability analysis.
              </p>
            </div>

            {hasValidCarInputs && (
              <div className="results-section">
                <h2>Affordability Results</h2>

                <div className="result-grid">
                  <div className="result-card">
                    <h3>Loan Amount</h3>
                    <p className="result-value">
                      {formatCurrency(carResult.loanAmount)}
                    </p>
                  </div>

                  <div className="result-card">
                    <h3>Monthly Repayment</h3>
                    <p className="result-value">
                      {formatCurrency(carResult.monthlyRepayment)}
                    </p>
                    <p className="result-detail">
                      Per month for {carInputs.repaymentTerm / 12}{" "}
                      {carInputs.repaymentTerm / 12 === 1 ? "year" : "years"}
                    </p>
                  </div>

                  {hasIncome && (
                    <div
                      className={`result-card affordability-${carResult.affordabilityStatus}`}
                    >
                      <h3>Affordability Status</h3>
                      <p className="result-value">
                        {carResult.affordabilityRatio.toFixed(1)}%
                      </p>
                      <p className="result-detail">
                        {carResult.affordabilityMessage}
                      </p>
                    </div>
                  )}

                  <div className="result-card">
                    <h3>Total Interest</h3>
                    <p className="result-value">
                      {formatCurrency(carResult.totalInterest)}
                    </p>
                    <p className="result-detail">
                      Over {carInputs.repaymentTerm} months
                    </p>
                  </div>

                  <div className="result-card">
                    <h3>Total Amount to Repay</h3>
                    <p className="result-value">
                      {formatCurrency(carResult.totalAmountToRepay)}
                    </p>
                    <p className="result-detail">Including balloon payment</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="calculator-section">
            <h2 className="visually-hidden">Home loan inputs</h2>

            <div className="input-group">
              <label htmlFor="home-income">
                Gross Monthly Income (Co-owner 1)
              </label>
              <input
                id="home-income"
                type="text"
                value={homeGrossMonthlyIncome}
                onChange={(e) => setHomeGrossMonthlyIncome(e.target.value)}
                placeholder="e.g., 40,000"
              />
              <p className="input-hint">
                Your total monthly income before any deductions
              </p>
            </div>

            <div className="input-group">
              <label htmlFor="home-income2">
                Gross Monthly Income (Co-owner 2) - Optional
              </label>
              <input
                id="home-income2"
                type="text"
                value={homeGrossMonthlyIncome2}
                onChange={(e) => setHomeGrossMonthlyIncome2(e.target.value)}
                placeholder="e.g., 35,000"
              />
              <p className="input-hint">
                For joint bonds or co-ownership, enter the second person's gross
                monthly income. Both incomes will be combined for the
                calculation.
              </p>
            </div>

            <div className="input-group">
              <label htmlFor="home-deposit">Deposit</label>
              <input
                id="home-deposit"
                type="text"
                value={homeDeposit}
                onChange={(e) => setHomeDeposit(e.target.value)}
                placeholder="e.g., 200,000"
              />
            </div>

            <div className="input-group">
              <label htmlFor="home-interest">Interest Rate (% p.a.)</label>
              <input
                id="home-interest"
                type="number"
                value={homeInterestRate}
                onChange={(e) => setHomeInterestRate(e.target.value)}
                placeholder="e.g., 11.5"
                step="0.01"
                min="0"
              />
            </div>

            <div className="input-group">
              <label htmlFor="home-term">Repayment Term (Years)</label>
              <select
                id="home-term"
                value={homeRepaymentTermYears}
                onChange={(e) => setHomeRepaymentTermYears(e.target.value)}
              >
                {Array.from({ length: 30 }, (_, i) => i + 1).map((year) => (
                  <option key={year} value={year}>
                    {year} {year === 1 ? "Year" : "Years"}
                  </option>
                ))}
              </select>
            </div>

            {hasValidHomeInputs && (
              <div className="results-section">
                <h2>Affordability Results</h2>

                <div className="result-grid">
                  <div
                    className={`result-card affordability-${homeResult.affordabilityStatus}`}
                  >
                    <h3>Max Affordable Home Price</h3>
                    <p className="result-value">
                      {formatCurrency(homeResult.maxAffordableHomePrice)}
                    </p>
                    <p className="result-detail">Including deposit</p>
                  </div>

                  <div className="result-card">
                    <h3>Maximum Loan Amount</h3>
                    <p className="result-value">
                      {formatCurrency(homeResult.maxLoanAmount)}
                    </p>
                    <p className="result-detail">Based on 30% of income</p>
                  </div>

                  <div className="result-card">
                    <h3>Estimated Monthly Repayment</h3>
                    <p className="result-value">
                      {formatCurrency(homeResult.monthlyRepayment)}
                    </p>
                    <p className="result-detail">
                      Over {homeInputs.repaymentTermYears} years
                    </p>
                  </div>

                  <div
                    className={`result-card affordability-${homeResult.affordabilityStatus}`}
                  >
                    <h3>Affordability Status</h3>
                    <p className="result-value">
                      {homeResult.affordabilityRatio.toFixed(1)}%
                    </p>
                    <p className="result-detail">
                      {homeResult.affordabilityMessage}
                    </p>
                  </div>

                  <div className="result-card">
                    <h3>Total Interest</h3>
                    <p className="result-value">
                      {formatCurrency(homeResult.totalInterest)}
                    </p>
                    <p className="result-detail">
                      Over {homeInputs.repaymentTermYears} years
                    </p>
                  </div>

                  <div className="result-card">
                    <h3>Total Loan Repayment</h3>
                    <p className="result-value">
                      {formatCurrency(homeResult.totalLoanRepayment)}
                    </p>
                    <p className="result-detail">Including interest</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="calculator-save-row">
          {loadedCalculationId ? (
            <>
              <button
                type="button"
                className="save-button"
                onClick={() => handleSaveClick(false)}
                disabled={!hasResult || saveStatus === "saving"}
              >
                {saveStatus === "saving" ? "Saving…" : saveStatus === "saved" ? "Saved" : saveStatus === "error" ? "Try again" : "Save"}
              </button>
              <button
                type="button"
                className="save-new-button"
                onClick={() => handleSaveClick(true)}
                disabled={!hasResult || saveStatus === "saving"}
              >
                Save new
              </button>
            </>
          ) : (
            <button
              type="button"
              className="save-button"
              onClick={() => handleSaveClick(false)}
              disabled={!hasResult || saveStatus === "saving"}
            >
              {saveStatus === "saving" ? "Saving…" : saveStatus === "saved" ? "Saved" : saveStatus === "error" ? "Try again" : "Save for later"}
            </button>
          )}
        </div>

        <AdSenseSlot className="AdSenseSlot-wrapper--in-panel" />
        <CalculatorSeoSection variant={isCar ? "car" : "home"} />
        <SiteFooter />
      </div>
    </main>
  );
};

export default Affordability;
